import { Component } from '@theme/component';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';

const COLLECTION_DESKTOP_SIZES = {
  roomy:
    '(min-width: 2000px) calc((100vw - 430px) / 3), (min-width: 1200px) calc((97vw - 370px) / 3), (min-width: 750px) calc((100vw - 406px) / 3), 50vw',
  default:
    '(min-width: 2000px) calc((100vw - 460px) / 4), (min-width: 1200px) calc((95.5vw - 370px) / 4), (min-width: 750px) calc((100vw - 424px) / 4), 50vw',
  compact:
    '(min-width: 2000px) calc((100vw - 490px) / 5), (min-width: 1200px) calc((94vw - 370px) / 5), (min-width: 750px) calc((100vw - 442px) / 5), 50vw',
};

/**
 * First-frame results-list behavior. Pagination is attached only when a real
 * previous/next page boundary approaches the viewport.
 */
export default class ResultsList extends Component {
  #imageSizeObserver = new MutationObserver(() => {
    const layout = this.refs.grid?.getAttribute('product-grid-view') || 'default';
    this.#syncCatalogImageSizes(layout);
  });

  /** @type {IntersectionObserver | undefined} */
  #paginationLoaderObserver;

  /** @type {Promise<unknown> | undefined} */
  #paginationRuntimePromise;

  /** @type {{ connect(): void, disconnect(): void, refresh(): void } | undefined} */
  #paginationController;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    const layout = this.refs.grid?.getAttribute('product-grid-view') || 'default';
    this.#syncCatalogImageSizes(layout);
    this.#updateImageSizeObserver(layout);
    this.#armPaginationRuntime();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refreshRuntime();
  }

  /**
   * Reconcile refs and runtime state after Collection filtering updates only
   * #ResultsList instead of replacing the <results-list> owner.
   */
  refreshAfterFilterUpdate() {
    super.updatedCallback();
    this.#refreshRuntime();
  }

  #refreshRuntime() {
    const layout = this.refs.grid?.getAttribute('product-grid-view') || 'default';
    this.#syncCatalogImageSizes(layout);
    this.#updateImageSizeObserver(layout);

    if (this.#paginationController) {
      this.#paginationController.refresh();
    } else {
      this.#armPaginationRuntime();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#imageSizeObserver.disconnect();
    this.#paginationLoaderObserver?.disconnect();
    this.#paginationController?.disconnect();
  }

  /**
   * Downloads pagination-only JS before the shopper reaches a valid page boundary,
   * while leaving the actual page HTML request behind the runtime's 600px observer.
   */
  #armPaginationRuntime() {
    if (this.#paginationController) {
      this.#paginationController.connect();
      return;
    }
    if (this.#paginationRuntimePromise) return;

    if (document.documentElement.classList.contains('shopify-design-mode')) {
      this.#ensurePaginationRuntime();
      return;
    }

    const { cards, grid, viewMorePrevious, viewMoreNext } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0 || !grid) return;

    const firstPage = Number(cards[0]?.dataset.page || 1);
    const lastRenderedPage = Number(cards[cards.length - 1]?.dataset.page || firstPage);
    const lastPage = Number(grid.dataset.lastPage || 1);

    const previousBoundary = firstPage > 1 ? viewMorePrevious : undefined;
    const nextBoundary = lastRenderedPage < lastPage ? viewMoreNext : undefined;
    if (!(previousBoundary instanceof Element) && !(nextBoundary instanceof Element)) return;

    if (!this.#paginationLoaderObserver) {
      this.#paginationLoaderObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.#ensurePaginationRuntime();
        },
        {
          rootMargin: '1400px 0px',
        }
      );
    }

    this.#paginationLoaderObserver.disconnect();
    if (previousBoundary instanceof Element) this.#paginationLoaderObserver.observe(previousBoundary);
    if (nextBoundary instanceof Element) this.#paginationLoaderObserver.observe(nextBoundary);
  }

  #ensurePaginationRuntime() {
    if (this.#paginationController) {
      this.#paginationController.connect();
      return Promise.resolve(this.#paginationController);
    }
    if (this.#paginationRuntimePromise) return this.#paginationRuntimePromise;

    this.#paginationLoaderObserver?.disconnect();
    this.#paginationRuntimePromise = import('@theme/paginated-list')
      .then(({ default: PaginatedListController }) => {
        const controller = new PaginatedListController(this);
        if (this.isConnected) controller.connect();
        this.#paginationController = controller;
        return controller;
      })
      .catch((error) => {
        this.#paginationRuntimePromise = undefined;
        if (this.isConnected) this.#armPaginationRuntime();
        throw error;
      });

    return this.#paginationRuntimePromise;
  }

  /**
   * Updates the layout.
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    this.#animateLayoutChange(target.value);
  }

  /** @param {string} value */
  #animateLayoutChange = async (value) => {
    if (!this.refs.grid) return;

    const isCatalogResults = Boolean(this.closest('[data-kervelan-catalog-results]'));

    if (isCatalogResults) {
      const grid = this.refs.grid;
      grid.classList.add('kv-product-grid--switching');
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      this.#setLayout(value);
      requestAnimationFrame(() => {
        grid.classList.remove('kv-product-grid--switching');
        this.#notifyProductGridLayoutChange();
      });
    } else {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
      this.#notifyProductGridLayoutChange();
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /** @param {string} value */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#syncCatalogImageSizes(value);
    this.#updateImageSizeObserver(value);
  }

  #notifyProductGridLayoutChange() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;
    grid.dispatchEvent(new CustomEvent('kervelan:product-grid-layout-change', { bubbles: true }));
  }

  /**
   * Keep eager catalog images aligned with the real 3/4/5-column desktop
   * grid, while preserving the mobile single-column 100vw upgrade.
   * @param {string} value
   */
  #syncCatalogImageSizes(value) {
    const { grid } = this.refs;
    if (!grid) return;

    const isCatalogResults = Boolean(this.closest('[data-kervelan-catalog-results]'));
    const desktopSizes = mediaQueryLarge.matches && isCatalogResults ? COLLECTION_DESKTOP_SIZES[value] : undefined;
    const singleMobile = !mediaQueryLarge.matches && value === 'mobile-single';

    for (const image of grid.querySelectorAll(
      'img.product-media__image[loading="eager"], img.kv-product-card__image--primary[loading="eager"]'
    )) {
      if (!(image instanceof HTMLImageElement)) continue;

      const sizes = image.getAttribute('sizes');
      if (!sizes) continue;
      if (!image.dataset.kvDefaultSizes) image.dataset.kvDefaultSizes = sizes;

      const defaultSizes = image.dataset.kvDefaultSizes;
      if (!defaultSizes) continue;

      let nextSizes = desktopSizes || defaultSizes;
      if (singleMobile) nextSizes = defaultSizes.replace(/,\s*50vw$/, ', 100vw');
      if (sizes !== nextSizes) image.setAttribute('sizes', nextSizes);
    }
  }

  /** @param {string} value */
  #updateImageSizeObserver(value) {
    this.#imageSizeObserver.disconnect();
    const { grid } = this.refs;
    if (!grid) return;

    const needsMobileSync = !mediaQueryLarge.matches && value === 'mobile-single';
    const needsDesktopSync =
      mediaQueryLarge.matches &&
      value !== 'default' &&
      Boolean(COLLECTION_DESKTOP_SIZES[value]) &&
      Boolean(this.closest('[data-kervelan-catalog-results]'));

    if (needsMobileSync || needsDesktopSync) {
      this.#imageSizeObserver.observe(grid, { childList: true, subtree: true });
    }
  }

  /** @param {MediaQueryListEvent} event */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
    this.#notifyProductGridLayoutChange();
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
